import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import apiClient, { post, endpoints, refreshTokens } from 'src/utils/api-client';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';


// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'access_token';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      let access_token = localStorage.getItem(STORAGE_KEY);

      // Access token expired but a valid refresh token may still renew it,
      // so the user isn't kicked back to login on every reload.
      if (!(access_token && isValidToken(access_token))) {
        access_token = await refreshTokens();
      }

      if (access_token && isValidToken(access_token)) {
        setSession(access_token);

        const response = await apiClient.get(endpoints.auth.me);

        const user = response.data;

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              access_token,
              ...user
            },
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const data = {
      email,
      password,
    };

    const response = await post(endpoints.auth.login, data);

    const { access_token, refresh_token } = response;

    setSession(access_token, refresh_token);

    await initialize();

  }, [initialize]);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await post(endpoints.auth.register, data);

    const { access_token, user } = response;

    localStorage.setItem(STORAGE_KEY, access_token);

    dispatch({
      type: 'REGISTER',
      payload: {
        user: {
          ...user,
          access_token,
        },
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // Revoke the session server-side before dropping the tokens (best-effort).
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      try {
        await post(endpoints.auth.logout, { refresh_token });
      } catch {
        /* ignore — local session is cleared regardless */
      }
    }
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
    }),
    [login, logout, register, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
